import { Box, FormGroup, NumberInput, Select } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils';
import { maxInt32 } from '@common/constants';
import { BatchType } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import type { BatchRun } from '../financingFlex/financingFlexSearchInputFilters';

export type BatchRunFilterProps = {
    batchRun?: BatchRun;
    errors: ValidationErrors;
    onChange: (value?: BatchRun) => void;
};

export function BatchRunFilter({ batchRun, errors, onChange }: BatchRunFilterProps) {
    const translate = getSelectedLanguage();
    const batchTypeOptions = getBatchTypeOptions();

    return (
        <FormGroup
            hasErrors={errors.has('batchRun.runNumber') || errors.has('batchRun.batchType')}
            label={translate.common.batchRun}
        >
            <Box sx={{ display: 'flex' }}>
                <Box width={'40%'}>
                    <NumberInput
                        error={getValidationErrorTranslation(errors.get('batchRun.runNumber'))}
                        label={translate.common.batchRunNumber}
                        maxValue={maxInt32}
                        minValue={0}
                        onChange={(value) => {
                            handleBatchRunFilter('runNumber', value);
                        }}
                        sx={{ marginRight: 2, width: 'unset' }}
                        value={batchRun?.runNumber ?? null}
                    />
                </Box>
                <Box width={'60%'}>
                    <Select
                        error={getValidationErrorTranslation(errors.get('batchRun.batchType'))}
                        isClearable={true}
                        onChange={(option) =>
                            handleBatchRunFilter('batchType', option ? (option.value as BatchType) : null)
                        }
                        options={batchTypeOptions}
                        placeholder={`${translate.common.select}...`}
                        value={batchTypeOptions.find((option) => option.value === batchRun?.batchType)}
                    />
                </Box>
            </Box>
        </FormGroup>
    );

    function handleBatchRunFilter<K extends keyof BatchRun>(key: K, value: BatchRun[K]) {
        const newBatchRun: BatchRun = {
            ...{
                batchType: null,
                runNumber: null,
            },
            ...batchRun,
            [key]: value,
        };

        onChange(newBatchRun.batchType === null && newBatchRun.runNumber === null ? undefined : newBatchRun);
    }

    function getBatchTypeOptions() {
        const options = Object.keys(BatchType)
            .filter((key) => key !== BatchType.payout)
            .map((key) => ({
                label: translate.batchType[key as BatchType],
                value: key,
            }));

        return options.sort((a, b) => a.label.localeCompare(b.label));
    }
}
